import { FC, useCallback, useEffect, useState } from "react";
import { Form } from "antd";
import { IProductIdentifier, EProductIdentifierType } from "../types";
import {
  addProductIdentifier,
  deleteProductIdentifier,
  getProductIdentifiers,
} from "../api";
import { Body2, Button2, Label } from "../../../components/Typography";
import { useTranslation } from "react-i18next";
import { LMNewButton } from "../../../components/LMNewButton";
import { LMNewInput } from "../../../components/LMNewInput";
import { LMDropdown } from "../../../components/LMDropdown";
import styles from "./ProductIdentifiers.module.css";
import { LMIconButton } from "../../../components/LMIconButton";
import { IconTrash } from "../../../assets";

interface Props {
  productId: number;
  onClose: () => void;
}

interface FormValues {
  type: EProductIdentifierType;
  value: string;
}

export const ProductIdentifiers: FC<Props> = ({ productId, onClose }) => {
  const [identifiers, setIdentifiers] = useState<IProductIdentifier[]>([]);
  const [loading, setLoading] = useState(false);
  const [form] = Form.useForm<FormValues>();
  const { t } = useTranslation("productsPortfolio");
  const { t: tLayout } = useTranslation("layout");

  const getIdentifiers = useCallback(async () => {
    const response = await getProductIdentifiers(productId);
    setIdentifiers(response.data);
  }, [productId]);

  useEffect(() => {
    getIdentifiers();
  }, [getIdentifiers]);

  const handleSubmit = async (values: FormValues) => {
    if (!values.value?.trim()) return;

    setLoading(true);
    try {
      await addProductIdentifier(productId, {
        type: values.type,
        value: values.value.trim(),
      });
      getIdentifiers();
      form.resetFields();
    } catch (error) {
      console.error("Error adding identifier:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (value: string) => {
    setLoading(true);
    try {
      await deleteProductIdentifier(productId, value);
      getIdentifiers();
    } catch (error) {
      console.error("Error deleting identifier:", error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.content}>
        {identifiers.map((identifier) => (
          <div
            className={styles.item}
            key={`${identifier.type}:${identifier.value}`}
          >
            <div className={styles["item-content"]}>
              <div className={styles.line}>
                <Body2 className={styles["item-title"]}>{t("type")}:</Body2>
                <Button2 className={styles["item-value"]}>
                  {identifier.type}
                </Button2>
              </div>
              <div className={styles.line}>
                <Body2 className={styles["item-title"]}>{t("value")}:</Body2>
                <Button2 className={styles["item-value"]}>
                  {identifier.value}
                </Button2>
              </div>
            </div>
            <LMIconButton onClick={() => handleDelete(identifier.value)}>
              <IconTrash />
            </LMIconButton>
          </div>
        ))}
      </div>
      <Form
        form={form}
        onFinish={handleSubmit}
        initialValues={{ type: EProductIdentifierType.SKU }}
        className={styles.form}
      >
        <Label className={styles.label}>{t("addNewIdentifier")}</Label>
        <Form.Item
          name="type"
          rules={[{ required: true, message: t("requiredMessage") }]}
        >
          <LMDropdown
            placeholder={t("selectIdentifierType")}
            items={Object.values(EProductIdentifierType).map((type) => ({
              name: type,
              value: type,
            }))}
          />
        </Form.Item>
        <Form.Item
          name="value"
          rules={[{ required: true, message: t("requiredMessage") }]}
        >
          <LMNewInput placeholder={t("enterIdentifierValue")} />
        </Form.Item>
      </Form>

      <div className={styles.buttons}>
        <LMNewButton
          color="default"
          onClick={onClose}
          className={styles.button}
        >
          {t("cancel")}
        </LMNewButton>

        <LMNewButton disabled={loading} onClick={() => form.submit()}>
          {loading ? tLayout("pleaseWait") : t("addIdentifier")}
        </LMNewButton>
      </div>
    </div>
  );
};
